import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useState } from 'react';
import { Modal } from '@instructure/ui-modal';
import { Button, CloseButton } from '@instructure/ui-buttons';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import { Link } from '@instructure/ui-link';
import { useStyle } from '@instructure/emotion';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
/**
---
category: components/AI Components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const NutritionFacts = ({
  modalLabel,
  title,
  featureName,
  data,
  closeButtonText,
  closeIconButtonScreenReaderLabel,
  triggerText,
  fullscreen = false
}) => {
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  const styles = useStyle({
    generateStyle,
    generateComponentTheme,
    componentId: 'NutritionFacts',
    displayName: 'NutritionFacts'
  });
  return _jsxs("div", {
    children: [_jsx(Link, {
      variant: "standalone",
      onClick: e => {
        e.preventDefault();
        setOpen(true);
      },
      forceButtonRole: false,
      href: "#",
      children: triggerText
    }), _jsxs(Modal, {
      open: open,
      onDismiss: () => {
        setOpen(false);
      },
      label: modalLabel,
      shouldCloseOnDocumentClick: true,
      size: fullscreen ? 'fullscreen' : 'medium',
      children: [_jsxs(Modal.Header, {
        spacing: "compact",
        children: [_jsx(Heading, {
          aiVariant: "stacked",
          children: title
        }), _jsx(CloseButton, {
          size: "medium",
          placement: "end",
          offset: "small",
          onClick: () => setOpen(false),
          screenReaderLabel: closeIconButtonScreenReaderLabel
        })]
      }), _jsx(Modal.Body, {
        children: _jsxs("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.body,
          children: [_jsxs(Heading, {
            variant: "titleSection",
            level: "h3",
            children: [' ', featureName, ' ']
          }), data.map(({
            blockTitle,
            segmentData
          }, index) => {
            return _jsxs("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.blockContainer,
              children: [_jsxs(Heading, {
                variant: "titleModule",
                level: "h4",
                children: [' ', blockTitle, ' ']
              }), _jsx("div", {
                css: styles === null || styles === void 0 ? void 0 : styles.segmentContainer,
                children: segmentData.map(({
                  segmentTitle,
                  description,
                  value,
                  valueDescription
                }, index) => {
                  return _jsxs("div", {
                    css: styles === null || styles === void 0 ? void 0 : styles.segmentCard,
                    children: [_jsxs("div", {
                      css: styles === null || styles === void 0 ? void 0 : styles.segmentCardExplainerContainer,
                      children: [_jsxs(Heading, {
                        variant: "label",
                        children: [' ', segmentTitle, ' ']
                      }), _jsxs(Text, {
                        variant: "contentSmall",
                        color: "secondary",
                        children: [' ', description, ' ']
                      })]
                    }), _jsxs(Text, {
                      variant: "content",
                      children: [" ", value, " "]
                    }), _jsxs(Text, {
                      variant: "contentSmall",
                      color: "secondary",
                      children: [' ', valueDescription, ' ']
                    })]
                  }, index);
                })
              })]
            }, index);
          })]
        })
      }), _jsx(Modal.Footer, {
        children: _jsx(Button, {
          onClick: () => setOpen(false),
          children: closeButtonText
        })
      })]
    })]
  });
};
export default NutritionFacts;
export { NutritionFacts };